import { useState } from "react";
import axios from "axios";
import { Check, Loader2, CheckCircle2, ArrowLeft, ArrowRight, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AGE_GROUPS, PROGRAM_TYPES, CONTACT } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Your group", "Program type", "Your details"];

export const InquiryForm = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    age_group: "", group_size: "", program_type: "",
    name: "", email: "", phone: "", organisation: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => {
    if (step === 0 && !form.age_group) return toast.error("Pick an age group to continue.");
    if (step === 1 && !form.program_type) return toast.error("Choose a program type to continue.");
    setStep((s) => Math.min(s + 1, 2));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) return toast.error("Please add your name, email and phone.");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/inquiries`, form);
      setDone(data);
    } catch {
      toast.error("Something went wrong — please call our director hotline instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="inquiry-success" className="rounded-3xl border border-[#CFE3E2] bg-white p-8 text-center shadow-xl">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#0E9E96]/12">
          <CheckCircle2 className="h-9 w-9 text-[#0E9E96]" />
        </div>
        <h3 className="mt-5 font-head text-2xl font-extrabold text-slate-900">Inquiry received!</h3>
        <p className="mt-2 text-sm text-slate-600">
          Your reference is <span className="font-extrabold text-slate-900" data-testid="inquiry-reference">{done.reference}</span>.
          We'll send your tailored quote within <strong>4 business hours</strong>.
        </p>
        <a href={CONTACT.phoneHref} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#F97316] px-6 py-3 text-sm font-extrabold text-white hover:bg-[#ea6a0c]">
          <Phone className="h-4 w-4" /> Or call now — {CONTACT.phone}
        </a>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-[#CFE3E2] bg-white p-6 shadow-xl sm:p-8" data-testid="inquiry-form">
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center gap-2">
            <div className={`grid h-8 w-8 flex-none place-items-center rounded-full text-sm font-extrabold ${i <= step ? "bg-[#0E9E96] text-white" : "bg-slate-100 text-slate-400"}`}>
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            {i < STEPS.length - 1 && <div className={`h-1 flex-1 rounded-full ${i < step ? "bg-[#0E9E96]" : "bg-slate-100"}`} />}
          </div>
        ))}
      </div>
      <p className="mt-3 font-head text-xl font-extrabold text-slate-900">
        Step {step + 1}: {STEPS[step]}
      </p>

      {step === 0 && (
        <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="step-age">
          {AGE_GROUPS.map((a) => (
            <button
              key={a.id}
              data-testid={`age-option-${a.id}`}
              onClick={() => set("age_group", a.label)}
              className={`rounded-2xl border-2 p-4 text-left transition-all ${form.age_group === a.label ? "border-[#0E9E96] bg-[#0E9E96]/5" : "border-[#E2ECEE] hover:border-[#0E9E96]/40"}`}
            >
              <p className="font-head text-base font-extrabold text-slate-900">{a.label}</p>
              <p className="text-xs text-slate-500">{a.desc}</p>
            </button>
          ))}
          <div className="sm:col-span-2">
            <Label className="mb-1.5 block text-xs font-extrabold uppercase tracking-wide text-slate-600">Approx. group size (optional)</Label>
            <Input data-testid="input-group-size" value={form.group_size} onChange={(e) => set("group_size", e.target.value)} placeholder="e.g. 20 children" />
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="mt-5 grid gap-3" data-testid="step-program">
          {PROGRAM_TYPES.map((p) => (
            <button
              key={p.id}
              data-testid={`program-option-${p.id}`}
              onClick={() => set("program_type", p.label)}
              className={`rounded-2xl border-2 p-4 text-left transition-all ${form.program_type === p.label ? "border-[#F97316] bg-[#F97316]/5" : "border-[#E2ECEE] hover:border-[#F97316]/40"}`}
            >
              <p className="font-head text-base font-extrabold text-slate-900">{p.label}</p>
              <p className="text-xs text-slate-500">{p.desc}</p>
            </button>
          ))}
        </div>
      )}

      {step === 2 && (
        <div className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="step-details">
          <Field label="Your name" req><Input data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" /></Field>
          <Field label="Phone" req><Input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="04xx xxx xxx" /></Field>
          <Field label="Email" req><Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" /></Field>
          <Field label="Centre / organisation"><Input data-testid="input-org" value={form.organisation} onChange={(e) => set("organisation", e.target.value)} placeholder="Optional" /></Field>
          <div className="sm:col-span-2">
            <Field label="Anything else?"><Textarea data-testid="input-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} placeholder="Preferred dates, suburb, goals..." /></Field>
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between gap-3">
        {step > 0 ? (
          <button data-testid="step-back" onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-extrabold text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        ) : <span />}
        {step < 2 ? (
          <Button data-testid="step-next" onClick={next} className="rounded-full bg-[#0E9E96] px-6 font-extrabold text-white hover:bg-[#0b847d]">
            Continue <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <Button data-testid="inquiry-submit" onClick={submit} disabled={submitting} className="rounded-full bg-[#F97316] px-6 font-extrabold text-white hover:bg-[#ea6a0c] disabled:opacity-70">
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Get my tailored quote"}
          </Button>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, req, children }) => (
  <div>
    <Label className="mb-1.5 block text-xs font-extrabold uppercase tracking-wide text-slate-600">
      {label} {req && <span className="text-[#F97316]">*</span>}
    </Label>
    {children}
  </div>
);
